/**
 * @fileoverview AR-924 — "Files and links:" block of the drawer: file grid, Classroom Drive attachments, links.
 */

import { Paper, SimpleGrid, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { ClassroomAttachmentLink } from 'components/ClassroomDeliverable'
import type { TaskTrackDelivery } from '../../types'
import { DeliveryFileCard } from './DeliveryFileCard'
import { DeliveryLinkCard } from './DeliveryLinkCard'

/** Figma: three cards per row on the 600px drawer. */
const GRID_COLUMNS = 3

interface FilesAndLinksSectionProps {
  readonly delivery: TaskTrackDelivery | null
  readonly readOnly: boolean
  readonly removingIds: ReadonlySet<string>
  readonly onRemoveFile: (fileId: string) => void
  readonly onRemoveLink: (linkId: string) => void
}

export function FilesAndLinksSection(props: FilesAndLinksSectionProps) {
  const { delivery, readOnly, removingIds, onRemoveFile, onRemoveLink } = props
  const { t } = useTranslation()
  const files = delivery?.files ?? []
  const links = delivery?.links ?? []
  const driveFiles = delivery?.googleDriveFiles ?? []
  const empty = files.length + links.length + driveFiles.length === 0

  return (
    <Stack spacing="sm">
      <Text size="sm" weight={700}>{t('task_tracker.teacher_upload.files_and_links')}</Text>
      {empty ? (
        <Paper withBorder radius="md" py="md" sx={theme => ({ background: theme.colors.gray[0], textAlign: 'center' })}>
          <Text size="sm" color="dimmed">{t('task_tracker.teacher_upload.nothing_added')}</Text>
        </Paper>
      ) : (
        <>
          {files.length > 0 && (
            <SimpleGrid cols={GRID_COLUMNS} spacing="sm">
              {files.map(file => (
                <DeliveryFileCard
                  key={file.id}
                  file={file}
                  readOnly={readOnly}
                  removing={removingIds.has(file.id)}
                  onRemove={onRemoveFile}
                />
              ))}
            </SimpleGrid>
          )}
          {driveFiles.map(driveFile => <ClassroomAttachmentLink key={driveFile.id} file={driveFile} />)}
          {links.map(link => (
            <DeliveryLinkCard
              key={link.id}
              link={link}
              readOnly={readOnly}
              removing={removingIds.has(link.id)}
              onRemove={onRemoveLink}
            />
          ))}
        </>
      )}
    </Stack>
  )
}
